import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { routes } from '../../routes/AppRoutes';
import { strings } from '../../resources/strings';
import { MainActions } from '../../actions/MainActions';
import { SessionHelper } from '../../helpers/SessionHelper';
import { OrderType } from '../../enums/OrderType';
import { CollectTrackingResultType } from '../../enums/CollectTrackingResultType';
import { StateOrderIdentification } from '../../enums/StateOrderIdentification';
import { storageKeys } from '../../resources/storageKeys';
import { StateLog } from '../../models/StateLog';
import ServicesContext from '../../contexts/ServicesContext';
import { useAuth } from '../../contexts/AuthContext';
import { Body } from '../../common-components/Layout/Body/Body';
import { Header } from '../../common-components/Layout/Header/Header';
import { Page } from '../../common-components/Layout/Page/Page';
import { IBreadcrumbItem } from '../../common-components/Breadcrumb/Breadcrumb';
import { ManageTransportHelper } from '../../helpers/ManageTransportHelper';
import { OrderInTransport } from '../../services/Orders/GetOrdersInTransport/OrderInTransport';
import { GetOrdersInTransportInput } from '../../services/Orders/GetOrdersInTransport/GetOrdersInTransportInput';
import { ActionsPanel } from '../../components/ActionsPanel/ActionsPanel';
import { AppliedFiltersToListOrdersInTransport } from './AppliedFiltersToListOrdersInTransport/AppliedFiltersToListOrdersInTransport';
import { IFilterToListOrdersInTransportOutput, ModalFiltersToListOrdersInTransport } from './ModalFiltersToListOrdersInTransport/ModalFiltersToListOrdersInTransport';
import { ActionsForOrdersInTransportSelection } from './ActionsForOrdersInTransportSelection/ActionsForOrdersInTransportSelection';
import { PaginatedOrdersInTransportList } from './PaginatedOrdersInTransportList/PaginatedOrdersInTransportList';
import { Container } from './styles';
import { ProfileType } from '../../enums/ProfileType';
import { PeriodTypeForOrdersInTransport } from '../../enums/PeriodTypeForOrdersInTransport';
import { omnichannelResources } from '../../resources/omnichannelResources';
import { FrontVersionResources } from '../../models/FrontVersion';

const ManageTransport: React.FC = () => {

  const defaultPeriodType: PeriodTypeForOrdersInTransport = PeriodTypeForOrdersInTransport.OrderDispatchedAndOrderCreated;
  const defaultShippingStartDate = new Date(2025, 0, 1);
  const valueForLastConsultStatusProblem = 'y';
  const valueForStatesIdProblem = `${StateOrderIdentification.EmTransporteAlertaInterno} ${StateOrderIdentification.EmTransporteAlertaInternoExterno}`;

  const dispatch = useDispatch();
  const mainActions = React.useMemo(() => new MainActions(dispatch), [dispatch]);

  const [version, setVersion] = React.useState<number>(1);
  const [ordersInTransport, setOrdersInTransport] = React.useState<OrderInTransport[]>([]);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [currentPageSize, setCurrentPageSize] = React.useState<number>(0);
  const [amountOfPages, setAmountOfPages] = React.useState<number>(0);
  const [totalAmount, setTotalAmount] = React.useState<number>(0);
  const [pageSize, setPageSize] = React.useState<number>(25);
  const [lastFilter, setLastFilter] = React.useState<GetOrdersInTransportInput | null>(null);
  const [lastFilterValues, setLastFilterValues] = React.useState<IFilterToListOrdersInTransportOutput | undefined>(undefined);
  const [modalFilterVisible, setModalFilterVisible] = React.useState<boolean>(false);
  const [filtering, setFiltering] = React.useState<boolean>(true);

  const navigate = useNavigate();
  const { session, signOut, userLocations } = useAuth();

  const {
    getOrdersInTransportService,
    toastService,
    storageService
  } = React.useContext(ServicesContext);

  const breadcrumbItems: IBreadcrumbItem[] = React.useMemo(() => {
    const memo: IBreadcrumbItem[] = [
      {
        text: strings.Home,
        route: routes.Home
      }
    ];
    return memo;
  }, []);

  useEffect(() => {
    if (!session || SessionHelper.isExpired(session)) {
      signOut(() => {
        navigate(routes.Login);
      });
      return;
    }

    clearFilter(pageSize, currentPage);
  }, [session, navigate]);

  const headerRender = React.useMemo(() => {
    return (
      <Header
        title={strings.ManageTransport}
        breadcrumbItems={breadcrumbItems}
        version={version}
        alertSummary={true}
      />
    );
  }, [version]);

  const memoActionsForOrderSelection = React.useMemo(() => {
    const stateFilterCodeLength = lastFilterValues?.stateFilterCode?.length || 0;
    const numberAppliedFilter =
      (lastFilterValues?.numbers ? 1 : 0) +
      (stateFilterCodeLength > 0 ? 1 : 0) +
      (lastFilterValues?.shippingStartDate ? 1 : 0) +
      (lastFilterValues?.shippingEndDate ? 1 : 0) +
      (lastFilterValues?.carrierCode ? 1 : 0) +
      (lastFilterValues?.virtualCollect ? 1 : 0);

    return (
      <ActionsForOrdersInTransportSelection
        filtering={filtering}
        appliedFilters={numberAppliedFilter}
        updateList={() => updateList(pageSize, currentPage)}
        filterList={() => setModalFilterVisible(true)}
        clearFilter={() => clearFilter(pageSize, currentPage)}
      />
    );

  }, [filtering, ordersInTransport, lastFilterValues]);

  var memoAppliedFiltersToListOrders = React.useMemo(() => {
    return <AppliedFiltersToListOrdersInTransport appliedFilters={lastFilterValues} />
  }, [lastFilter, lastFilterValues, filtering, ordersInTransport, currentPage, currentPageSize, pageSize]);

  var memoModalFilter = React.useMemo(() => {
    if (!modalFilterVisible) return null;

    return (
      <ModalFiltersToListOrdersInTransport
        appliedFilters={lastFilterValues}
        close={() => setModalFilterVisible(false)}
        finish={(values) => changeFilter(values)}
      />
    );
  }, [modalFilterVisible, lastFilter, lastFilterValues, filtering, ordersInTransport]);

  const bodyRender = React.useMemo(() => {

    return (
      <Container>
        {memoModalFilter}
        {memoAppliedFiltersToListOrders}
        <ActionsPanel>
          {memoActionsForOrderSelection}
        </ActionsPanel>
        <PaginatedOrdersInTransportList
          currentPage={currentPage}
          currentPageSize={currentPageSize}
          amountOfPages={amountOfPages}
          totalAmount={totalAmount}
          ordersInTransport={ordersInTransport}
          filterAreaHeight={0}
          pageSize={pageSize}
          changePageSize={changePageSize}
          changePage={changePage}
          newStateOrder={(state) => setNewStateOrder(state)}
          filtering={filtering}
          refreshRow={(orderInTransport) => refreshRow(orderInTransport)}
        />
      </Container>
    );
  }, [ordersInTransport, currentPage, currentPageSize, amountOfPages, pageSize, totalAmount, filtering, modalFilterVisible]);

  return (
    <Page header={headerRender}>
      <Body>
        {bodyRender}
      </Body>
    </Page>
  );

  function changePage(page: number) {
    setCurrentPage(page);
    updateList(pageSize, page);
  }

  function changePageSize(size: number) {
    setPageSize(size);
    setCurrentPage(1);
    updateList(size, 1);
  }

  function clearFilter(size: number, page: number) {
    setCurrentPage(page);

    const filterOrdersWithTransportProblem = storageService.get(storageKeys.FilterOrdersWithTransportProblemOnEnteringTransportManagementPage);

    const inititalFilter = filterOrdersWithTransportProblem ? {
      shippingStartDate: defaultShippingStartDate,
      stateIds: valueForStatesIdProblem,
      fulfilledBy: '',
      periodType: defaultPeriodType,
    } : {
      shippingStartDate: defaultShippingStartDate,
      fulfilledBy: '',
      periodType: defaultPeriodType,
    };

    if (session?.user?.profileType === ProfileType.Vendor || (session?.user?.profileType === ProfileType.Ecommerce && userLocations.length > 0)) {
      inititalFilter.fulfilledBy = omnichannelResources.FulfillmentGroupOptions.MyStoreAndEcommerce.filterId;
    }

    setLastFilterValues(inititalFilter);
    filter(inititalFilter, size, page);
  }

  function updateList(size: number, page: number) {
    if (SessionHelper.isExpired(session)) {
      navigate(routes.Login);
      return;
    }
    setCurrentPage(page);
    filter(lastFilter || {}, size, page);
  }

  function changeFilter(values: IFilterToListOrdersInTransportOutput) {
    setModalFilterVisible(false);
    setCurrentPage(1);
    setLastFilterValues(values);
    let pageSizeValue = pageSize;
    if (values.numbers) {
      setPageSize(0);
      pageSizeValue = 0;
    }
    filter(values, pageSizeValue, 1);
  }

  function filter(values: IFilterToListOrdersInTransportOutput, size: number, page: number) {
    mainActions.setNotWait(true);
    storageService.set(storageKeys.FilterOrdersWithTransportProblemOnEnteringTransportManagementPage);
    const filterInput = new GetOrdersInTransportInput();
    filterInput.pageSize = size > 0 ? size : 1;
    filterInput.currentPage = page;
    filterInput.shouldPaginate = size > 0;
    filterInput.frontVersion = FrontVersionResources.currentNumber;
    filterInput.numbers = values.numbers ? values.numbers.replaceAll('#', '').trim() : undefined;
    const orderStateFilterValues = ManageTransportHelper.getOrderStateFilterValues();
    const statesId = orderStateFilterValues.find(x => x.code == values.stateFilterCode)?.states || orderStateFilterValues[0].states;
    filterInput.stateIds = statesId && statesId.length > 0 ? statesId.join(' ') : undefined;
    filterInput.stateFilterCode = values.stateFilterCode;
    filterInput.shippingStartDate = values.shippingStartDate ? moment(values.shippingStartDate).format('yyyy-MM-DD') : undefined;
    filterInput.shippingEndDate = values.shippingEndDate ? moment(values.shippingEndDate).format('yyyy-MM-DD') : undefined;
    filterInput.carrierCode = values.carrierCode;
    filterInput.virtualCollect = values.virtualCollect;
    filterInput.collectNotDone = values.virtualCollect === 'NotDone' ? false : null;
    filterInput.collectTrackingResultType = null;
    if (values.virtualCollect && values.virtualCollect !== 'NotDone') {
      switch (values.virtualCollect) {
        case CollectTrackingResultType[CollectTrackingResultType.Failed]:
          filterInput.collectTrackingResultType = CollectTrackingResultType.Failed;
          break;
        case CollectTrackingResultType[CollectTrackingResultType.PendingSystem]:
          filterInput.collectTrackingResultType = CollectTrackingResultType.PendingSystem;
          break;
        case CollectTrackingResultType[CollectTrackingResultType.PendingUser]:
          filterInput.collectTrackingResultType = CollectTrackingResultType.PendingUser;
          break;
        case CollectTrackingResultType[CollectTrackingResultType.Success]:
          filterInput.collectTrackingResultType = CollectTrackingResultType.Success;
          break;
        default:
          filterInput.collectTrackingResultType = null;
      }
    }
    filterInput.lastConsultStatus = values.lastConsultStatus;
    filterInput.lastConsultStatusIsProblem = values.lastConsultStatus
      ? values.lastConsultStatus === valueForLastConsultStatusProblem
      : null;
    filterInput.periodType = values.periodType;
    filterInput.userId = session?.user?.id;
    filterInput.locationId = !values.locationId && session?.user?.profileType === ProfileType.Admin 
      ? null
      : userLocations.length == 1 
        ? userLocations[0] 
        : values.locationId;
    filterInput.fulfilledBy = values.fulfilledBy;
    filterInput.originedBy = values.originedBy;
    filterInput.includesCrossChannelSale = null;
    filterInput.onlyCrossChannelSale = false;
    filterInput.fulfillmentLocationIds = null;
    if (session?.user?.profileType === ProfileType.Vendor) {
      if (values.fulfilledBy === omnichannelResources.FulfillmentGroupOptions.MyStoreAndEcommerce.filterId) {
        filterInput.includesCrossChannelSale = true;
        filterInput.fulfillmentLocationIds = [ 1 ];
      } else if (values.fulfilledBy === omnichannelResources.FulfillmentGroupOptions.Ecommerce.filterId) {
        filterInput.includesCrossChannelSale = true;
        filterInput.onlyCrossChannelSale = true;
        filterInput.fulfillmentLocationIds = [ 1 ];
      }
    } else if (session?.user?.profileType === ProfileType.Ecommerce) {
      filterInput.includesCrossChannelSale = true;
      filterInput.onlyCrossChannelSale = null;
      if (values.originedBy == omnichannelResources.OrderOriginGroupOptions.Ecommerce.filterId) {
        filterInput.includesCrossChannelSale = false;
      } else if (values.originedBy == omnichannelResources.OrderOriginGroupOptions.PhysicalStores.filterId) {
        filterInput.onlyCrossChannelSale = true;
      }
    } else if (session?.user?.profileType === ProfileType.Admin) {
      filterInput.includesCrossChannelSale = true;
      filterInput.onlyCrossChannelSale = null;
    }
    setLastFilter(filterInput);
    handleFilterOrder(filterInput);
  }

  function handleFilterOrder(filterInput: GetOrdersInTransportInput) {
    mainActions.setNotWait(true);
    setFiltering(true);
    filterInput.orderType = OrderType.Ascending;
    getOrdersInTransportService.handle(filterInput)
      .then(result => {
        setOrdersInTransport(result?.value?.list || []);
        setCurrentPage(result?.value?.currentPage || 0);
        setCurrentPageSize(result?.value?.currentPageSize || 0);
        setAmountOfPages(result?.value?.amountOfPages || 0);
        setTotalAmount(result?.value?.totalAmount || 0);
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongGettingTheData);
      })
      .finally(() => {
        setTimeout(() => mainActions.setNotWait(false), 1000);
        setFiltering(false);
      });
  }

  function setNewStateOrder(state: StateLog) {
    const newOrdersInTransport = ordersInTransport.map(orderInTransport => {
      if (orderInTransport.orderId === state.orderId) {
        orderInTransport.currentState.currentStateId = state.currentStateId;
        orderInTransport.currentState.description = state.currentStateName;
        orderInTransport.currentState.createdAt = state.createdAt;
      }
      return orderInTransport;
    });
    setOrdersInTransport(newOrdersInTransport);
    setVersion(Date.now);
  }

  function refreshRow(orderInTransport?: OrderInTransport | null) {
    if (!orderInTransport) return;

    setOrdersInTransport(ordersInTransport.map(oit => {
      if (oit.orderId !== orderInTransport.orderId) return oit;
      return orderInTransport;
    }));
  }
}

export default ManageTransport;